import React, { useCallback, useState } from 'react';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { useAuth } from 'auth/context/AuthContext';
import {
  FhirQuestionnaireNames,
  useGetFhirQuestionnaireResponseQuery,
} from 'graphql/generated/remote-schema-hasura';
import { GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS } from 'utilities/constants';
import ButtonComponent from 'components/button/buttonComponent';
import { useNavigate } from 'react-router-dom';
import { MY_ACCOUNT_PROFILE } from 'utilities/routes';
import { useVerifyChild } from './useVerifyChild';
import { Roles } from '../../../../firebase/interfaces';

const ChildQuestionnaireBanner = () => {
  const { selectedAccount, user: loggedUser } = useAuth();
  const { isVerify } = useVerifyChild();

  const navigate = useNavigate();
  const [hasQuestionnaires, setHasQuestionnaires] = useState<boolean>(true);

  const handleGoToHealthQuestionnaire = useCallback(
    () => navigate(`${MY_ACCOUNT_PROFILE}/questionnaire`),
    [navigate],
  );

  useGetFhirQuestionnaireResponseQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    variables: {
      codexPatientId: selectedAccount || loggedUser?.uuid || '',
      identifier: FhirQuestionnaireNames.HealthQuestionnaire,
      system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
    onCompleted(data) {
      if (
        data &&
        data.getFHIRQuestionnaireResponse.questionnaire_response.responses
          .length
      ) {
        return setHasQuestionnaires(true);
      }
      setHasQuestionnaires(false);
    },
    onError() {
      setHasQuestionnaires(false);
    },
    skip: !loggedUser?.uuid || loggedUser?.role !== Roles.PATIENT,
  });

  if (!hasQuestionnaires && isVerify) {
    return (
      <div className="bg-white flex flex-col desktop:flex-row rounded-10 p-30 gap-30">
        <div className="flex items-center desktop:justify-start justify-between gap-2.5 flex-row w-full desktop:w-auto">
          <h3 className="text-h3 font-semibold desktop:whitespace-nowrap text-neutral-800">
            {'Child’s health questionnaire'}
          </h3>
          <span className="rounded-full whitespace-nowrap px-2.5 gap-2.5 border border-med-gray-3 text-med-gray-3 bg-base-content">
            {'Not filled'}
          </span>
        </div>
        <div className="flex items-start desktop:items-center desktop:justify-start justify-between gap-2.5 flex-row w-full desktop:w-auto">
          <InfoIcon className="fill-amber-border w-5 h-5 min-w-[20px]" />
          <p className="text-amber-border text-sm font-semibold">
            {
              'Fill in the questionnaire to help your provider better understand your child’s symptoms and make an accurate diagnosis.'
            }
          </p>
        </div>
        <ButtonComponent
          testID="fill-questionaire"
          className="desktop:whitespace-nowrap text-sm"
          onClick={handleGoToHealthQuestionnaire}
        >
          Fill in questionnaire
        </ButtonComponent>
      </div>
    );
  }

  return null;
};

export default ChildQuestionnaireBanner;
